import { Component } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
} from "@angular/forms";
import {
  ValidationErrors,
  ValidationService,
} from "src/app/core/validation/validation.service";
import { Router } from "@angular/router";
import { first } from "rxjs/operators";
import { ModalService } from "src/app/core/modalservice/modal.service";
import { UserFacadeService } from "src/app/core/facades/userfacade/userfacade.service";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { MatFormField, MatLabel, MatError } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-initialpasswordreset",
  templateUrl: "./initialpasswordreset.component.html",
  styleUrls: ["./initialpasswordreset.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgFor,
    NgIf,
    NgClass,
    MatError,
    TranslatePipe,
  ],
})
export class InitialpasswordresetComponent {
  public passwordForm: UntypedFormGroup;
  public errorList;
  public newPassword: string;
  public passwordIsVisible: boolean = false;
  public passwordLengthIsValid: boolean = true;
  public passwordLowerValid: boolean = true;
  public passwordSymbolIsValid: boolean = true;
  public passwordUpperIsValid: boolean = true;
  public passwordNumberIsValid: boolean = true;
  constructor(
    private formBuilder: UntypedFormBuilder,
    public oidcSecurityService: OidcSecurityService,
    private router: Router,
    private userFacadeService: UserFacadeService,
    private modalService: ModalService,
  ) {
    this.errorList = ValidationErrors.validMsg;
    this.passwordForm = this.formBuilder.group(
      {
        password: [],
        newPassword: [
          "",
          Validators.compose([
            Validators.required,
            Validators.minLength(12),
            this.contains(/[A-Z]/, "upper"),
            this.contains(/[a-z]/, "lower"),
            this.contains(/\d/, "number"),
            this.contains(/[-#$^+=!()@%&?_*.;[\]{}|<>~:"/\\€°]/, "symbol"),
          ]),
        ],
        confirmPassword: [],
      },
      {
        validator: ValidationService.matchingPassword("newPassword", "confirmPassword"),
      },
    );
  }

  updatePassword() {
    if (this.passwordForm.valid) {
      this.userFacadeService
        .initialUserPasswordChange(
          this.userFacadeService.user.getValue().id,
          this.passwordForm.controls.password.value,
          this.passwordForm.controls.newPassword.value,
        )
        .pipe(first())
        .subscribe(
          (resp) => {
            this.modalService.successModal();
            this.router.navigate(["/home"]);
          },
          (err) => {
            this.modalService.errorModal(err);
          },
        );
    }
  }
  navigateToLogin() {
    this.oidcSecurityService.logoff().subscribe();
  }
  private contains(regexp: RegExp, key: string) {
    return (ctrl: AbstractControl): ValidationErrors | null =>
      !ctrl.value || regexp.test(ctrl.value) ? null : { [key]: true };
  }
  get strength(): string {
    if (!this.newPassword) return "none";
    const len = this.newPassword.length;
    if (len >= 16) return "strong";
    if (len >= 14) return "medium";
    return "weak";
  }

  get errors() {
    return this.passwordForm.get("newPassword")?.errors ?? {};
  }

  togglePasswordVisibility() {
    this.passwordIsVisible = !this.passwordIsVisible;
  }
  copyToClipboard(event) {
    navigator.clipboard
      .writeText(this.newPassword)
      .then(() => {
        const popup = document.createElement("div");
        popup.textContent = "Copied!";
        popup.style.position = "absolute";
        popup.style.backgroundColor = "#333";
        popup.style.color = "#fff";
        popup.style.padding = "5px 10px";
        popup.style.borderRadius = "5px";
        popup.style.fontSize = "12px";
        popup.style.boxShadow = "0 2px 5px rgba(0,0,0,0.3)";
        popup.style.transform = "translate(-50%, -100%)"; // Adjust position above the click point
        popup.style.pointerEvents = "none"; // Prevent blocking clicks
        popup.style.zIndex = "1000";
        popup.style.opacity = "1";
        popup.style.transition = "opacity 0.3s ease";

        // Position the popup near the click
        popup.style.left = `${event.pageX}px`;
        popup.style.top = `${event.pageY}px`;

        document.body.appendChild(popup);

        // Fade out and remove the popup after 1.5 seconds
        setTimeout(() => {
          popup.style.opacity = "0";
          setTimeout(() => {
            popup.remove();
          }, 300); // Match the fade-out duration
        }, 1500);
      })
      .catch((error) => {
        console.error("Failed to copy password: ", error);
      });
  }
  generatePasword() {
    // Define character pools
    const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerCase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@@#$%^&*()_+[]{}|;:,.<>?";

    // get random character from a string
    function getRandomChar(charSet) {
      return charSet[getSecureRandomIndex(charSet.length)];
    }
    // Helper function to get cryptographically secure random index
    function getSecureRandomIndex(max) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      return array[0] % max;
    }

    let password = "";

    // Ensure at least one of each character type
    password += getRandomChar(upperCase);
    password += getRandomChar(lowerCase);
    password += getRandomChar(numbers);
    password += getRandomChar(symbols);

    // Fill the rest of the password with random characters
    const allCharacters = upperCase + lowerCase + numbers + symbols;
    for (let i = 4; i < 16; i++) {
      password += getRandomChar(allCharacters);
    }

    // Fisher-Yates shuffle using cryptographically secure random numbers
    const passwordArray = password.split("");
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = getSecureRandomIndex(i + 1);
      [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]]; // Swap elements
    }
    // Set the generated password in the input field
    this.newPassword = passwordArray.join("");
    //this.validatePassword();
  }
}
